import React, { Fragment } from "react";
import PromptPanel from "./PromptPanel.tsx";
import ShowMemories from "./ShowMemories.tsx";
import Graph from "./Graph.tsx";
import { Exchange } from "../../store/types.tsx";
import Table from "./Table.tsx";
import SignUpOptions from "../search/signup/SignUpOptions.tsx";
import AvatarPanel from "./AvatarPanel.tsx";
import { Box } from "@mui/material";
import { useTypedSelector } from "../../hooks.tsx";
import {
  getIsConversationWorking,
  getSelectedConversationExchanges,
} from "../../store/conversation.tsx";

interface ResponsePromptProps {
  children?: React.ReactNode;
  exchange: Exchange;
}

const ResponsePrompt: React.FC<ResponsePromptProps> = (props) => {
  let content = null;
  let type = null;

  // Tally is working on this answer while the selected conversation is still
  // streaming and this is the newest exchange in it.
  const isSelectedConversationWorking = useTypedSelector((state) =>
    getIsConversationWorking(
      state,
      state.conversationState.selectedConversation,
    ),
  );
  const isLatestExchange = useTypedSelector((state) => {
    const allIds = getSelectedConversationExchanges(state)?.allIds;
    return !!allIds && allIds[allIds.length - 1] === props.exchange.id;
  });
  const working = isSelectedConversationWorking && isLatestExchange;

  // Add logging to track type changes that might cause unmount/remount
  const prevContentRef = React.useRef<string | null>(null);
  if (
    props.exchange.json_response !== undefined &&
    props.exchange.json_response !== null
  ) {
    content = props.exchange.json_response.content;
    type = props.exchange.json_response.type;
  } else if (
    props.exchange.response !== undefined &&
    props.exchange.response !== null
  ) {
    content = props.exchange.response;
    type = props.exchange.type;
  }

  // Prevent unmounting during streaming by preserving last non-empty content
  if (!content && prevContentRef.current) {
    console.log(
      "🔄 ResponsePrompt: Using previous content to prevent unmount",
      {
        exchange_id: props.exchange.id,
        previousContent:
          (prevContentRef.current?.substring(0, 50) || "") + "...",
      },
    );
    content = prevContentRef.current;
  }

  // Store content for future use if it's not empty
  if (content && typeof content === "string" && content.trim() !== "") {
    prevContentRef.current = content;
  }

  if (type === "SignUpOptions") {
    return (
      <PromptPanel
        key={`assistant_${props.exchange.id}`}
        role="assistant"
        content={(content as string) || ""}
        customContent={<SignUpOptions />}
        exchange_id={props.exchange.id}
        feedback={props.exchange.feedback}
        user_comment={props.exchange.user_comment}
        progress_update={props.exchange.progress_update}
        isStreaming={props.exchange.json_response?.isStreaming || false}
        working={working}
      />
    );
  } else if (!content) {
    // Nothing has streamed in yet: show the mark on its own, pulsing, so the
    // answer's place is held while Tally works.
    return working ? (
      <Box sx={{ marginBottom: "4%" }}>
        <AvatarPanel working />
      </Box>
    ) : (
      <Fragment />
    );
  } else if (
    type === undefined ||
    type === "TextResponse" ||
    type === "StreamingTextResponse"
  ) {
    // Consolidated handling for all text responses to prevent unmount/remount
    return (
      <PromptPanel
        key={`assistant_${props.exchange.id}`}
        role="assistant"
        content={content as string}
        exchange_id={props.exchange.id}
        feedback={props.exchange.feedback}
        user_comment={props.exchange.user_comment}
        progress_update={props.exchange.progress_update}
        isStreaming={props.exchange.json_response?.isStreaming || false}
        working={working}
      ></PromptPanel>
    );
  } else if (type === "Memory") {
    return <ShowMemories role="assistant" content={content as Array<any>} />;
  } else if (type === "Graph") {
    return (
      <Graph
        content={content}
        feedback={props.exchange.feedback}
        exchange_id={props.exchange.id}
      />
    );
  } else if (type === "Table") {
    return (
      <Table
        content={content}
        feedback={props.exchange.feedback}
        exchange_id={props.exchange.id}
      />
    );
  }
};

export default React.memo(ResponsePrompt);
